import {
    DotsHorizontalIcon
} from '@radix-ui/react-icons';
import {
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { useContext, useEffect, useState } from 'react';
import '../../App.css';
import { AuthContext } from '../../context/AuthContext';
import api from '../../lib/api';
import LendBunnyDialog from '../dialogs/lendBunnyDialog';
import ReturnLoanedBunnyDialog from '../dialogs/returnLoanedBunnyDialog';
import { Button } from '../ui/button';
import {
    Dialog,
} from "../ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent, DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from "../ui/dropdown-menu";
import { Input } from '../ui/input';
import { DataTable } from './datatable';
import DatatableSortButton from './datatableSortButton';

export const columns = (fetchDrivers, bunny) => [
    {
        accessorKey: "name",
        header: ({ column }) => <DatatableSortButton column={column}>Utlånad till</DatatableSortButton>,
        cell: info => `${info.row.original.first_name} ${info.row.original.last_name}`,
    },
    {
        accessorKey: "email",
        header: ({ column }) => <DatatableSortButton column={column}>E-post</DatatableSortButton>,
    },
    {
        accessorKey: "created_at",
        header: ({ column }) => <DatatableSortButton column={column}>Utlåningsdatum</DatatableSortButton>,
        cell: info => {
            const driver = info.row.original;
            const date = new Date(driver.created_at);
            return date.toLocaleDateString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            });
        }
    },
    {
        id: "actions",
        enableHiding: false,
        cell: ({ row }) => {
            const driver = row.original;
            return (
                <Dialog modal={true}>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 float-right">
                                <span className="sr-only">Öppna meny</span>
                                <DotsHorizontalIcon className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onCloseAutoFocus={(e) => {
                            e.preventDefault();
                        }}>
                            <DropdownMenuLabel>Alternativ</DropdownMenuLabel>

                            <DropdownMenuSeparator />
                            <ReturnLoanedBunnyDialog bunny={bunny} driver={driver} fetchDrivers={fetchDrivers} />
                        </DropdownMenuContent>
                    </DropdownMenu>
                </Dialog>
            );
        }
    },
];

export function LoanedToDatatable({ bunny }) {
    const [sorting, setSorting] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [columnFilters, setColumnFilters] = useState([]);
    const [filterInput, setFilterInput] = useState("");
    const { auth } = useContext(AuthContext);

    const fetchDrivers = async () => {
        try {
            const response = await api.get(`https://skhrf-api.cmdigital.se/v1/bunnies/drivers?bunnyId=${bunny.id}`);
            const drivers = response.data.drivers.map(driver => ({
                ...driver,
                name: `${driver.first_name} ${driver.last_name}`
            }));

            setDrivers(drivers);
        } catch (error) {
            console.error('Error fetching drivers:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDrivers();
    }, [bunny]);

    // Delay function before filtering to reduce bounce
    useEffect(() => {
        const handler = setTimeout(() => {
            table.getColumn("name")?.setFilterValue(filterInput);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [filterInput]);

    const table = useReactTable({
        data: drivers,
        columns: columns(fetchDrivers, bunny),
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            sorting,
            columnFilters,
        },
    });

    return (
        <div className='rounded-md bg-gray-100 p-3'>
            <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 py-3'>
                <Input
                    id='searchDriver'
                    placeholder="Sök användare..."
                    value={filterInput}
                    disabled={isLoading}
                    onChange={event => setFilterInput(event.target.value)}
                    className="max-w-sm"
                />
                <LendBunnyDialog bunny={bunny} fetchDrivers={fetchDrivers} className='xl:col-start-3' />
            </div>
            <DataTable
                table={table}
                isLoading={isLoading}
                columns={columns(fetchDrivers, bunny)}
            />
        </div>
    );
}
